/**
 * Daily Red Report (spec §17.2). CR-2026-GATE-OVERRIDE (2026-10-14). Server: GET /api/gate-overrides/red-report?date=
 * Built from the audit log and gate_overrides only. Sections the system does not feed yet are named on the page,
 * never shown as zero.
 */
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link as RouterLink } from "react-router-dom";
import { Alert, Box, Button, Card, Chip, Container, Grid, Stack, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from "@mui/material";
import ApiCall from "../../utils/apicall";

import SmartTable from "../../components/SmartTable/SmartTable";
const msgOf = (e, f) => e?.response?.data?.message || e?.message || f;
const when = (d) => (d ? String(d).replace("T", " ").slice(0, 16) : "");
const todayLocal = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };

function Blocks({ title, rows }) {
  return (
    <Card sx={{ p: 2, mb: 2 }}>
      <Typography variant="h6">{title} ({rows.length})</Typography>
      {rows.length === 0 ? <Typography variant="caption" color="text.secondary">None on this date.</Typography> : (
        <SmartTable tableId="red-gate-blocks" lockedColumns={["Time", "Rule", "Job"]} size="small">
          <TableHead><TableRow><TableCell>Time</TableCell><TableCell>Rule</TableCell><TableCell>Job</TableCell><TableCell>Object</TableCell><TableCell>By</TableCell></TableRow></TableHead>
          <TableBody>
            {rows.map((r, i) => (
              <TableRow key={i}>
                <TableCell>{when(r.at)}</TableCell><TableCell>{r.rule}</TableCell><TableCell>{r.jobName || r.jobId || ""}</TableCell>
                <TableCell>{r.objectType} {r.objectId}</TableCell><TableCell>{r.actor || ""}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </SmartTable>
      )}
    </Card>
  );
}

export default function RedReport() {
  const [date, setDate] = useState(todayLocal());
  const [data, setData] = useState(null);
  const [msg, setMsg] = useState(null);
  useEffect(() => {
    setMsg(null);
    ApiCall.get(`/gate-overrides/red-report?date=${date}`).then((r) => setData(r.data.data)).catch((e) => { setData(null); setMsg(msgOf(e, "Could not build the report.")); });
  }, [date]);
  const s = data?.summary || {};
  const tiles = [
    ["Overrides used", s.overridesUsed], ["Quarantined heat issue attempts", s.quarantinedIssueAttempts], ["Heat mismatches at the Saw", s.heatMismatches],
    ["Traceability blocks", s.traceabilityBlocks], ["Grade blocks", s.gradeBlocks], ["Open overrides", s.openOverrides],
    ["Contract review blocks", s.contractReviewBlocks], ["Jobs without an accepted F1.6.0", s.jobsWithoutAcceptedContractReview], ["F1.6.0 traceability mismatches", s.contractReviewTraceMismatches],
  ];
  return (
    <>
      <Helmet><title>Daily Red Report | SFM</title></Helmet>
      <Container maxWidth="xl">
        <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ md: "center" }} spacing={1.5} sx={{ mb: 2 }}>
          <Box>
            <Typography variant="h4">Daily Red Report</Typography>
            <Typography variant="body2" color="text.secondary">Overrides, blocked attempts and open items for one day (server date).</Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <TextField size="small" type="date" label="Date" value={date} onChange={(e) => setDate(e.target.value)} InputLabelProps={{ shrink: true }} />
            <Button component={RouterLink} to="/gate-overrides" variant="outlined">Gate overrides</Button>
            <Button variant="outlined" onClick={() => window.print()}>Print</Button>
          </Stack>
        </Stack>
        {msg && <Alert severity="error" sx={{ mb: 2 }}>{msg}</Alert>}
        {data && (
          <>
            <Grid container spacing={1.5} sx={{ mb: 2 }}>
              {tiles.map(([label, n]) => (
                <Grid item xs={6} md={2} key={label}>
                  <Card sx={{ p: 1.5, borderLeft: 4, borderColor: n ? "error.main" : "divider" }}>
                    <Typography variant="h4">{n ?? 0}</Typography><Typography variant="caption">{label}</Typography>
                  </Card>
                </Grid>
              ))}
            </Grid>
            <Card sx={{ p: 2, mb: 2 }}>
              <Typography variant="h6">Overrides used ({data.overridesUsed.length})</Typography>
              {data.overridesUsed.length === 0 ? <Typography variant="caption" color="text.secondary">None on this date.</Typography> : (
                <SmartTable tableId="red-overrides" lockedColumns={["Time", "#", "Gate", "Heat", "QC co-sign"]} size="small">
                  <TableHead><TableRow><TableCell>Time</TableCell><TableCell>#</TableCell><TableCell>Gate</TableCell><TableCell>Job</TableCell><TableCell>Heat</TableCell><TableCell>Reason</TableCell><TableCell>Requested by</TableCell><TableCell>QC co-sign</TableCell></TableRow></TableHead>
                  <TableBody>
                    {data.overridesUsed.map((o, i) => (
                      <TableRow key={i}>
                        <TableCell>{when(o.at)}</TableCell><TableCell>{o.overrideId}</TableCell><TableCell>{o.gate}</TableCell><TableCell>{o.jobName || o.jobId}</TableCell>
                        <TableCell>{o.heatNumber || ""}</TableCell><TableCell>{o.reason}</TableCell><TableCell>{o.requestedBy}</TableCell><TableCell>{o.cosignedBy}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </SmartTable>
              )}
            </Card>
            <Card sx={{ p: 2, mb: 2 }}>
              <Typography variant="h6">Open overrides ({data.openOverrides.length})</Typography>
              {data.openOverrides.length === 0 ? <Typography variant="caption" color="text.secondary">None.</Typography> : data.openOverrides.map((o) => (
                <Stack key={o.id} direction="row" spacing={1} alignItems="center" sx={{ py: 0.5 }}>
                  <Chip size="small" label={o.status} color={o.status === "REQUESTED" ? "warning" : "info"} />
                  <Typography variant="body2">#{o.id} {o.type === "HEAT_ISSUE" ? `heat ${o.heatNumber || o.heatId}` : o.type === "CLOSEOUT" ? "job closeout" : o.type === "REOPEN" ? "job reopen" : o.type === "CONTRACT_REVIEW" ? "contract review gate" : `${o.itemIds.length} mark(s)`}, {o.jobName || o.jobId}, {o.ageHours} h old, requested by {o.requestedByName}</Typography>
                </Stack>
              ))}
            </Card>
            <Blocks title="Quarantined heat issue attempts (ISS-PT-01)" rows={data.quarantinedIssueAttempts} />
            <Blocks title="Heat mismatches at the Saw" rows={data.heatMismatches} />
            <Blocks title="Traceability blocks (TRC)" rows={data.traceabilityBlocks} />
            <Blocks title="Grade blocks (REQ-TR-004, no override)" rows={data.gradeBlocks} />
            <Blocks title="Two-person rule blocks" rows={data.twoPersonBlocks} />
            <Blocks title="Contract review blocks (CR-V01, sign-off refusals)" rows={data.contractReviewBlocks || []} />
            <Card sx={{ p: 2, mb: 2 }}>
              <Typography variant="h6">Jobs without an accepted F1.6.0 contract review ({(data.contractReviewOpen || []).length})</Typography>
              {(data.contractReviewOpen || []).length === 0 ? <Typography variant="caption" color="text.secondary">None.</Typography> : (
                <SmartTable tableId="red-gated-jobs" lockedColumns={["Job", "Gated", "Age (days)"]} size="small">
                  <TableHead><TableRow><TableCell>Job</TableCell><TableCell>Gated</TableCell><TableCell>Review</TableCell><TableCell>Age (days)</TableCell><TableCell>Note</TableCell></TableRow></TableHead>
                  <TableBody>
                    {data.contractReviewOpen.map((j) => (
                      <TableRow key={j.jobId}>
                        <TableCell><RouterLink to={`/jobs/${j.jobId}/contract-review`}>{j.jobName || j.jobId}</RouterLink></TableCell>
                        <TableCell>{j.gated ? "Yes" : "No (existed at go-live)"}</TableCell>
                        <TableCell>{j.status === "NONE" ? "None" : `Rev ${j.revision}: ${j.status.toLowerCase()}`}</TableCell>
                        <TableCell>{j.ageDays}</TableCell><TableCell>{j.note}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </SmartTable>
              )}
            </Card>
            {(data.contractReviewTraceMismatches || []).length > 0 && (
              <Card sx={{ p: 2, mb: 2 }}>
                <Typography variant="h6">F1.6.0 accepted with a different traceability level than the job ({data.contractReviewTraceMismatches.length})</Typography>
                <Typography variant="caption" color="text.secondary">The job's level was not changed. The QM resolves which one the contract requires.</Typography>
                <SmartTable tableId="red-contract-levels" lockedColumns={["When", "Job", "Last signer"]} size="small">
                  <TableHead><TableRow><TableCell>When</TableCell><TableCell>Job</TableCell><TableCell>Job level</TableCell><TableCell>F1.6.0 says</TableCell><TableCell>Last signer</TableCell></TableRow></TableHead>
                  <TableBody>
                    {data.contractReviewTraceMismatches.map((m, i) => (
                      <TableRow key={i}>
                        <TableCell>{new Date(m.at).toLocaleString()}</TableCell>
                        <TableCell><RouterLink to={`/jobs/${m.jobId}/contract-review`}>{m.jobName || m.jobId}</RouterLink></TableCell>
                        <TableCell>{m.jobLevel}</TableCell><TableCell>{m.reviewLevel}</TableCell><TableCell>{m.lastSigner}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </SmartTable>
              </Card>
            )}
            <Blocks title="Other blocks" rows={data.otherBlocks} />
            <Alert severity="info">
              Not in this report yet: {data.notInThisReport.join(" ")}
            </Alert>
          </>
        )}
      </Container>
    </>
  );
}
